import { Capacitor } from '@capacitor/core'
import { useAuthState } from '@healthblocks-io/core'
import { useCallback, useEffect, useRef } from 'react'

import { mixpanel } from '@/mixpanel'
import { useLogout } from '@/modules/auth/hooks/useLogout'
import { IDLE_TIMEOUT, WARNING_PERCENTAGE } from '@/modules/core/constants/idle-timer.constants'
import { useDebugSettings } from '@/modules/profile/hooks/useDebugSettings'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useNavigate } from '@/router-wrapper'
import { isProduction } from '@/utils/environment'
import { useIdleTimer } from '../hooks/useIdleTimer'
import { IdleTimerWarningModal } from './IdleTimerWarningModal'

import type { ReactNode } from 'react'

export const IdleTimer = ({ children }: { children: ReactNode }) => {
  const visible = useRef(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const navigate = useNavigate()
  const { logout } = useLogout()
  const { setLockout } = useLockoutTime()
  const { debugSettings } = useDebugSettings()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  // Template variables
  // ------------------------------------------------------------------------- /
  const idleTimeout = isProduction()
    ? // default 30 minutes
      IDLE_TIMEOUT
    : debugSettings?.idleTimeout || IDLE_TIMEOUT

  const warningPercentage = isProduction() ? WARNING_PERCENTAGE : debugSettings?.warningPercentage || WARNING_PERCENTAGE

  const authState = useAuthState()

  const setVisible = (value: boolean) => {
    visible.current = value
  }
  const isRunning = !!authState

  // Interaction handlers
  // ------------------------------------------------------------------------- /
  const handleLogout = useCallback(async () => {
    if (Capacitor.isNativePlatform() && !isLoggedInForMandatee) {
      mixpanel.track('account_lockscreen', { automatic: true })
      setLockout(true)
      setVisible(false)
      navigate('/login')
    } else {
      mixpanel.track('account_logout', { automatic: true })
      setVisible(false)
      await logout()
    }
  }, [isLoggedInForMandatee, logout, navigate, setLockout])

  const { timeRemaining, suspendInteraction, resumeInteraction } = useIdleTimer(handleLogout, isRunning, idleTimeout)

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (isRunning && timeRemaining < idleTimeout * warningPercentage) {
      suspendInteraction()
      setVisible(true)

      return
    }

    setVisible(false)
  }, [timeRemaining, suspendInteraction, isRunning, idleTimeout, warningPercentage])

  // Render
  // ------------------------------------------------------------------------- /
  return visible.current ? (
    <IdleTimerWarningModal
      timeRemaining={timeRemaining}
      warningPercentage={warningPercentage}
      timeout={idleTimeout}
      isVisible={visible.current}
      onClose={resumeInteraction}
    />
  ) : (
    <>{children}</>
  )
}
